import { useCallback, useMemo } from "react";
import intl from "react-intl-universal";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import {
  buildBaasSwapAssetsFromWallets,
  buildBaasWalletAccountData,
  buildBaasWalletStablesAsset,
  createBaasStablesQuote,
  executeBaasStablesOperation,
  resolveBaasWalletChainId,
} from "../../../../../Services/baasStablesWalletApi";
import {
  buildWithdrawQuote,
} from "../../../Stables/StablesQuoteModal";
import { StablesOperationsPanel } from "../../../Stables/StablesOperationsPanel";
import {
  isBaasBuyLimitConfigured,
  resolveBaasBuyLimitAvailableCents,
} from "../baasStablesLimitsConstants";

/**
 * BaaS admin wrapper around the client StablesOperationsPanel.
 * Same buy/sell/swap/transfer UI and flow; live BaaS quote + confirm (FoxBit settlement stubbed).
 * Buy/sell/withdraw: single asset from the admin-selected wallet (selector above).
 * Swap: lists stables from owned active wallets (De/Para), not a market catalog.
 * `brlBalance` must be linked fiat available in centavos (same as IB Contas).
 */
export const BaasStablesOperationsPanel = ({
  wallet,
  wallets = [],
  operation,
  marketQuotes = [],
  marketQuotesLoading = false,
  brlBalance = 0,
  brlBalanceFormatted,
  loadingBalance = false,
  onOperationConfirmed,
  /** Mapped GET …/limits for the wallet account (compra disponível for buy max / %). */
  entityLimits = null,
}) => {
  const { selectedInstance } = useBaasInstance();
  const accountData = useMemo(() => buildBaasWalletAccountData(wallet), [wallet]);
  const selectedAsset = useMemo(
    () => (wallet ? buildBaasWalletStablesAsset(wallet, marketQuotes) : null),
    [wallet, marketQuotes],
  );
  const preferredChainId = useMemo(
    () => (wallet ? resolveBaasWalletChainId(wallet) : null),
    [wallet],
  );

  const buyLimitAvailableCents = useMemo(
    () => resolveBaasBuyLimitAvailableCents(entityLimits),
    [entityLimits],
  );
  const buyLimitConfigured = useMemo(
    () => isBaasBuyLimitConfigured(entityLimits),
    [entityLimits],
  );

  const assetsOverride = useMemo(() => {
    if (!selectedAsset) return [];
    if (operation === "swap") {
      // Stable order; pinSelected keeps each asset's walletId for execute, without remounting page.
      const walletAssets = buildBaasSwapAssetsFromWallets(
        wallets,
        marketQuotes,
        wallet,
        { pinSelected: false },
      );
      return walletAssets.length ? walletAssets : [selectedAsset];
    }
    return [selectedAsset];
  }, [selectedAsset, operation, wallets, marketQuotes, wallet]);

  const createQuoteFn = useCallback(
    async ({
      operation: quoteOperation,
      selectedAsset: asset,
      selectedChain,
      tradeTransaction,
      withdrawTransaction,
      swapTransaction,
      fromAsset,
      toAsset,
      inputCurrency,
      numericAmount,
    }) => {
      if (!wallet || !selectedInstance || (quoteOperation !== "swap" && !asset)) {
        return null;
      }

      if (quoteOperation === "buy" || quoteOperation === "sell") {
        if (!tradeTransaction) return null;
        return await createBaasStablesQuote({
          instance: selectedInstance,
          wallet,
          operation: quoteOperation,
          amount: numericAmount,
          inputCurrency,
          transaction: tradeTransaction,
        });
      }

      if (quoteOperation === "swap") {
        if (!fromAsset || !toAsset || !swapTransaction) return null;
        const fromWallet =
          wallets.find((item) => item.id === fromAsset.walletId) || wallet;
        return await createBaasStablesQuote({
          instance: selectedInstance,
          wallet: fromWallet,
          operation: "swap",
          amount: numericAmount,
          inputCurrency: "stable",
          transaction: swapTransaction,
          fromAsset,
          toAsset,
        });
      }

      if (quoteOperation === "withdraw") {
        if (!selectedChain || !withdrawTransaction) return null;
        const quote = await createBaasStablesQuote({
          instance: selectedInstance,
          wallet,
          operation: "withdraw",
          amount: numericAmount,
          inputCurrency: "stable",
          transaction: withdrawTransaction,
        });
        return buildWithdrawQuote({
          asset,
          transaction: {
            ...quote,
            ...withdrawTransaction,
            chain: selectedChain.label,
          },
        });
      }

      return null;
    },
    [wallet, wallets, selectedInstance],
  );

  const confirmFn = useCallback(
    async ({ operation: quotedOperation, quote }) => {
      // Swap uses the "De" asset wallet; do not remount/navigate on De/Para change.
      let opWallet = wallet;
      let toWallet = null;
      if (quotedOperation === "swap") {
        const fromId = quote?.fromAssetId;
        const toId = quote?.toAssetId;
        const fromAsset =
          (fromId && assetsOverride.find((item) => item.id === fromId)) || null;
        const toAsset =
          (toId && assetsOverride.find((item) => item.id === toId)) || null;
        if (fromAsset?.walletId) {
          const matched = wallets.find((item) => item.id === fromAsset.walletId);
          if (matched) opWallet = matched;
        }
        if (toAsset?.walletId) {
          toWallet = wallets.find((item) => item.id === toAsset.walletId) || null;
        }
      }

      const result = await executeBaasStablesOperation({
        instance: selectedInstance,
        wallet: opWallet,
        operation: quotedOperation,
        quote,
        toWallet,
      });

      const labels = {
        buy: intl.get("baas.stables.operation.confirm.buy"),
        sell: intl.get("baas.stables.operation.confirm.sell"),
        swap: intl.get("baas.stables.operation.confirm.swap"),
        withdraw: intl.get("baas.stables.operation.confirm.withdraw"),
      };
      const label = labels[quotedOperation] || intl.get("baas.stables.operation.confirm.generic");

      return {
        ...result,
        message: intl.get("baas.stables.operation.success", { label }),
        description:
          quotedOperation === "withdraw"
            ? intl.get("baas.stables.operation.successWithdrawDescription")
            : intl.get("baas.stables.operation.successDescription"),
      };
    },
    [wallet, wallets, assetsOverride, selectedInstance],
  );

  const operationLabels = useMemo(
    () => ({
      buy: intl.get("baas.stables.operation.confirm.buy"),
      sell: intl.get("baas.stables.operation.confirm.sell"),
      swap: intl.get("baas.stables.operation.confirm.swap"),
      withdraw: intl.get("baas.stables.operation.confirm.withdraw"),
    }),
    [],
  );

  if (!wallet) {
    return (
      <section className="app-card app-empty-state">
        <p>{intl.get("baas.stables.operation.selectWalletFirst")}</p>
      </section>
    );
  }

  if (wallet.status && wallet.status !== "active") {
    return (
      <section className="app-card app-empty-state">
        <p>{intl.get("baas.stables.operation.walletInactive", { status: wallet.status })}</p>
      </section>
    );
  }

  if (!selectedAsset) {
    return null;
  }

  // Swap: key by operation only so De/Para never remounts the form.
  // Buy/sell/transfer: still remount when the page wallet selector changes.
  const panelKey = operation === "swap" ? `swap-${wallet.entityId || wallet.accountId || "entity"}` : `${wallet.id}-${operation}`;

  return (
    <StablesOperationsPanel
      key={panelKey}
      operation={operation}
      brlBalance={brlBalance}
      brlBalanceFormatted={brlBalanceFormatted}
      loadingBalance={loadingBalance}
      accountData={accountData}
      marketQuotes={marketQuotes}
      marketQuotesLoading={marketQuotesLoading}
      onOperationConfirmed={onOperationConfirmed}
      assetsOverride={assetsOverride}
      preferredAssetId={operation === "swap" ? null : selectedAsset.id}
      preferredChainId={preferredChainId}
      restrictChainsToPreferred={operation !== "swap"}
      createQuoteFn={createQuoteFn}
      confirmFn={confirmFn}
      operationLabels={operationLabels}
      withdrawSubmitLabel={intl.get("baas.stables.operation.confirmWithdraw")}
      buyLimitAvailableCentsOverride={
        operation === "buy" ? buyLimitAvailableCents : null
      }
      buyLimitConfiguredOverride={
        operation === "buy" ? buyLimitConfigured : null
      }
    />
  );
};
